/**
 * Global Android Back Button handler hook
 * 
 * Behavior:
 * - On root screens: exit the app
 * - On non-root screens: navigate back in history
 * 
 * Supports:
 * - Median JavaScript Bridge (preferred)
 * - Browser popstate event (fallback)
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isRootScreen } from '@/lib/constants/rootScreens';
import { 
  hasMedianBridge, 
  setMedianBackHandler, 
  exitApp 
} from '@/lib/utils/medianBridge';
import { isWebView } from '@/lib/utils/webview';

export function useAndroidBackHandler(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const isHandlingBack = useRef(false);

  const handleBack = useCallback(() => {
    // Prevent double-handling
    if (isHandlingBack.current) return;
    isHandlingBack.current = true;

    const currentPath = location.pathname;
    
    if (import.meta.env.DEV) {
      console.log('[AndroidBack]', {
        path: currentPath,
        isRoot: isRootScreen(currentPath),
        action: isRootScreen(currentPath) ? 'exit' : 'goBack'
      });
    }
    
    if (isRootScreen(currentPath)) {
      // On root screen → exit app
      exitApp();
    } else {
      // Not on root → navigate back
      navigate(-1);
    }

    // Reset flag after short delay
    setTimeout(() => {
      isHandlingBack.current = false;
    }, 300);
  }, [location.pathname, navigate]);

  useEffect(() => {
    // --- TEMPORARY DEBUG: Log environment on mount ---
    console.log('[AndroidBack] 🔧 Hook mounted', {
      pathname: location.pathname,
      isWebView: isWebView(),
      hasMedianBridge: hasMedianBridge(),
      isRoot: isRootScreen(location.pathname),
    });

    // --- TEMPORARY: Comment out for browser testing ---
    // if (!isWebView()) return;

    // Method 1: Median Bridge (preferred)
    if (hasMedianBridge()) {
      console.log('[AndroidBack] 📱 Using Median Bridge');
      setMedianBackHandler(handleBack);
      
      return () => {
        setMedianBackHandler(null);
      };
    }

    // Method 2: History API popstate (fallback)
    console.log('[AndroidBack] 🌐 Using popstate fallback');
    
    const handlePopState = (event: PopStateEvent) => {
      const path = window.location.pathname;
      const isRoot = isRootScreen(path);
      
      console.log('[AndroidBack] ⬅️ popstate event', {
        path,
        isRoot,
        action: isRoot ? 'EXIT' : 'GO_BACK',
      });

      if (isRoot) {
        event.preventDefault();
        console.log('[AndroidBack] 🚪 Would exit app (root screen)');
        exitApp();
      }
      // Otherwise, let normal history navigation happen
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      console.log('[AndroidBack] 🧹 Cleanup');
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleBack, location.pathname]);
}
